import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Lightbulb,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Download,
  Settings,
  FileDown,
  Palette,
  Upload,
  Database,
  Monitor,
  ChevronDown,
  Search,
  Shield,
  Map,
  BarChart3
} from 'lucide-react';
import { processOptimizationAPI, OptimizationAnalysis, OptimizationOpportunity } from '../services/processOptimizationAPI';
import OpportunityCard from '../components/ProcessOptimization/OpportunityCard';
import ExecutiveSummary from '../components/ProcessOptimization/ExecutiveSummary';
import PrioritizationMatrix from '../components/ProcessOptimization/PrioritizationMatrix';
import GanttRoadmap from '../components/ProcessOptimization/GanttRoadmap';
import MetricsImportModal from '../components/ProcessOptimization/MetricsImportModal';
import GroundedStatusBadge, { GroundedProgress } from '../components/ProcessOptimization/GroundedStatusBadge';
import { metricsImportAPI, GroundedStatus, ImportHistoryEntry } from '../services/metricsImportAPI';
import { useProject } from '../contexts/ProjectContext';
import toast from 'react-hot-toast';
import * as XLSX from '../utils/xlsxCompat';
import { generateMetricsTemplate } from '../utils/metricsTemplateGenerator';
import { generateAllOpportunitiesReport } from '../services/reportGenerators';

const sortByDependencies = (opportunities: OptimizationOpportunity[]): OptimizationOpportunity[] => {
  if (!opportunities || opportunities.length === 0) return [];

  const result: OptimizationOpportunity[] = [];
  const added = new Set<number>();
  const remaining = [...opportunities];

  const getPrerequisiteIds = (opp: OptimizationOpportunity): number[] => {
    if (!opp.dependencies?.prerequisites) return [];
    return opp.dependencies.prerequisites
      .map(p => p.prerequisite_opportunity?.id || p.id)
      .filter((id): id is number => id !== undefined && id !== null);
  };

  const canBeAdded = (opp: OptimizationOpportunity): boolean => {
    const prereqIds = getPrerequisiteIds(opp);
    if (prereqIds.length === 0) return true;
    return prereqIds.every(prereqId => added.has(prereqId));
  };

  let iterations = 0;
  const maxIterations = opportunities.length * 2;

  while (remaining.length > 0 && iterations < maxIterations) {
    iterations++;
    let addedThisRound = false;

    for (let i = remaining.length - 1; i >= 0; i--) {
      const opp = remaining[i];
      if (canBeAdded(opp)) {
        result.push(opp);
        added.add(opp.id);
        remaining.splice(i, 1);
        addedThisRound = true;
      }
    }

    if (!addedThisRound && remaining.length > 0) {
      const next = remaining.shift()!;
      result.push(next);
      added.add(next.id);
    }
  }

  return result;
};

type ViewMode = 'all' | 'business' | 'compliance' | 'matrix' | 'roadmap';

const ProcessOptimization: React.FC = () => {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const { currentProject } = useProject();
  const navigate = useNavigate();

  const projectId = urlProjectId || currentProject?.id?.toString();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<OptimizationAnalysis | null>(null);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('processOptimization_viewMode');
    return (saved as ViewMode) || 'all';
  });
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const prefetchedRef = useRef(false);
  const [groundedStatus, setGroundedStatus] = useState<GroundedStatus | null>(null);
  const [importHistory, setImportHistory] = useState<ImportHistoryEntry[]>([]);
  const [isDownloadingPptx, setIsDownloadingPptx] = useState(false);
  const [mainTab, setMainTab] = useState<'opportunities' | 'matrix' | 'roadmap'>('opportunities');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [reviewedIds, setReviewedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (projectId) {
      loadAnalysis();
      loadGroundedStatus();
      loadImportHistory();
    }
  }, [projectId]);

  const loadGroundedStatus = async () => {
    try {
      const status = await metricsImportAPI.getGroundedStatus(parseInt(projectId!));
      setGroundedStatus(status);
    } catch (error) {
      console.error('Error loading grounded status:', error);
    }
  };

  const loadImportHistory = async () => {
    try {
      const history = await metricsImportAPI.getImportHistory(parseInt(projectId!));
      setImportHistory(history);
    } catch (error) {
      console.error('Error loading import history:', error);
    }
  };

  const handleImportComplete = async () => {
    await Promise.all([loadAnalysis(), loadGroundedStatus(), loadImportHistory()]);
    setWizardStep(4);
  };

  const prefetchComplianceData = (opportunities: OptimizationOpportunity[], pid: number) => {
    if (prefetchedRef.current || !opportunities?.length) return;
    prefetchedRef.current = true;

    // Fire-and-forget: warm up DB cache for MRM + SOD for each opportunity
    // Small delay so page render isn't blocked
    setTimeout(() => {
      opportunities.forEach(opp => {
        // MRM classification (GET)
        fetch(`/api/process-optimization/${pid}/opportunities/${opp.id}/mrm-classification`, {
          credentials: 'include'
        }).catch(() => {});

        // SOD analysis (POST)
        fetch(`/api/process-optimization/${pid}/opportunities/${opp.id}/sod-analysis`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            affectedSteps: opp.affected_steps || [],
            solutionApproach: opp.solution_approach
          })
        }).catch(() => {});
      });
    }, 1500);
  };

  const loadAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await processOptimizationAPI.getAnalysis(parseInt(projectId!));

      console.log('[ProcessOptimization] API Response:', result);
      console.log('[ProcessOptimization] Analysis data:', result.data);
      if (result.data?.opportunities && result.data.opportunities.length > 0) {
        const firstOpp = result.data.opportunities[0];
        console.log('[ProcessOptimization] First opportunity:', {
          id: firstOpp.id,
          title: firstOpp.title,
          governance_complexity_score: firstOpp.governance_complexity_score,
          automation_readiness_score: firstOpp.automation_readiness_score,
          integration_complexity_score: firstOpp.integration_complexity_score,
          regulatory_risk_score: firstOpp.regulatory_risk_score,
          has_phased_plan: !!firstOpp.phased_rollout_plan,
          phased_plan_keys: firstOpp.phased_rollout_plan ? Object.keys(firstOpp.phased_rollout_plan) : []
        });
      }

      if (result.needsGeneration) {
        // No analysis exists — start at Step 1
        setWizardStep(1);
      } else {
        setAnalysis(result.data!);
        // Do NOT set wizardStep here — computedWizardStep + useEffect handle auto-advance
        // Pre-fetch compliance data in background so Compliance tab is instant
        prefetchComplianceData(result.data!.opportunities, parseInt(projectId!));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async (forceRegenerate: boolean) => {
    try {
      setAnalyzing(true);
      setError(null);

      const result = await processOptimizationAPI.analyzeProject(
        parseInt(projectId!),
        undefined,
        forceRegenerate
      );

      setAnalysis(result.data);
      setWizardStep(2); // advance to Review after analysis
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to run analysis';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setAnalyzing(false);
    }
  };

  const getInefficiencyIcon = (type: string) => {
    const icons: any = {
      'redundancy': '🔄',
      'sequential_bottleneck': '⚡',
      'manual_pattern': '✋',
      'loop_detection': '🔁',
      'handoff_delay': '🤝',
      'exception_handling': '⚠️'
    };
    return icons[type] || '📋';
  };

  const getInefficiencyLabel = (type: string) => {
    const labels: any = {
      'redundancy': 'Redundancy',
      'sequential_bottleneck': 'Sequential Bottleneck',
      'manual_pattern': 'Manual Process',
      'loop_detection': 'Rework Loop',
      'handoff_delay': 'Handoff Delay',
      'exception_handling': 'Exception Handling'
    };
    return labels[type] || type;
  };

  const exportToExcel = async () => {
    if (!analysis || !analysis.opportunities || analysis.opportunities.length === 0) {
      return;
    }

    // Format data for Excel — include grounded financial fields when available
    const excelData = analysis.opportunities.map((opp, index) => {
      const isGrounded = opp.data_source === 'grounded';
      const row: Record<string, any> = {
        'No.': index + 1,
        'Problem': opp.title,
        'Pattern Type': getInefficiencyLabel(opp.inefficiency_type),
        'Description': opp.description,
        'Affected Steps': opp.affected_steps?.length || 0,
        'Recommended Action': opp.solution_approach,
        'Complexity': opp.implementation_complexity?.toUpperCase() || 'N/A',
        'Time Reduction': opp.estimated_time_reduction || 'N/A',
        'Recommended Tools': opp.primary_tools?.join(', ') || 'N/A',
        'Alternative Tools': opp.alternative_tools?.join(', ') || 'N/A',
        'AI Integration': opp.ai_integration_opportunities || 'N/A',
        // Financial columns
        'Data Source': isGrounded ? 'Grounded' : 'Estimated',
        'Annual Benefit ($)': isGrounded && opp.total_annual_benefit != null ? Number(opp.total_annual_benefit) : '',
        'NPV 3yr ($)': isGrounded && opp.npv_3year != null ? Number(opp.npv_3year) : '',
        'ROI 3yr (%)': isGrounded && opp.roi_3year_pct != null ? Number(opp.roi_3year_pct) : '',
        'Payback (months)': isGrounded && opp.payback_months != null ? Number(opp.payback_months) : '',
        'Annual Hours Saved': isGrounded && opp.annual_hours_saved != null ? Number(opp.annual_hours_saved) : '',
        'Weighted Hourly Rate ($)': isGrounded && opp.weighted_hourly_rate != null ? Number(opp.weighted_hourly_rate) : '',
        // Monte Carlo Sensitivity columns
        'MC NPV P10 ($)': isGrounded && opp.mc_npv_p10 != null ? Number(opp.mc_npv_p10) : '',
        'MC NPV P50 ($)': isGrounded && opp.mc_npv_p50 != null ? Number(opp.mc_npv_p50) : '',
        'MC NPV P90 ($)': isGrounded && opp.mc_npv_p90 != null ? Number(opp.mc_npv_p90) : '',
        'MC ROI P10 (%)': isGrounded && opp.mc_roi_p10 != null ? Number(opp.mc_roi_p10) : '',
        'MC ROI P50 (%)': isGrounded && opp.mc_roi_p50 != null ? Number(opp.mc_roi_p50) : '',
        'MC ROI P90 (%)': isGrounded && opp.mc_roi_p90 != null ? Number(opp.mc_roi_p90) : '',
        'MC Payback P10 (mo)': isGrounded && opp.mc_payback_p10 != null ? Number(opp.mc_payback_p10) : '',
        'MC Payback P50 (mo)': isGrounded && opp.mc_payback_p50 != null ? Number(opp.mc_payback_p50) : '',
        'MC Payback P90 (mo)': isGrounded && opp.mc_payback_p90 != null ? Number(opp.mc_payback_p90) : '',
        'Prob Positive ROI (%)': isGrounded && opp.mc_prob_positive_roi != null ? Number((opp.mc_prob_positive_roi * 100).toFixed(1)) : '',
        'Prob Payback <12mo (%)': isGrounded && opp.mc_prob_payback_12mo != null ? Number((opp.mc_prob_payback_12mo * 100).toFixed(1)) : '',
      };
      return row;
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // No.
      { wch: 50 },  // Problem
      { wch: 20 },  // Pattern Type
      { wch: 60 },  // Description
      { wch: 15 },  // Affected Steps
      { wch: 60 },  // Recommended Action
      { wch: 12 },  // Complexity
      { wch: 20 },  // Time Reduction
      { wch: 30 },  // Recommended Tools
      { wch: 30 },  // Alternative Tools
      { wch: 40 },  // AI Integration
      { wch: 12 },  // Data Source
      { wch: 18 },  // Annual Benefit ($)
      { wch: 14 },  // NPV 3yr ($)
      { wch: 14 },  // ROI 3yr (%)
      { wch: 18 },  // Payback (months)
      { wch: 20 },  // Annual Hours Saved
      { wch: 22 },  // Weighted Hourly Rate ($)
      { wch: 16 },  // MC NPV P10
      { wch: 16 },  // MC NPV P50
      { wch: 16 },  // MC NPV P90
      { wch: 16 },  // MC ROI P10
      { wch: 16 },  // MC ROI P50
      { wch: 16 },  // MC ROI P90
      { wch: 18 },  // MC Payback P10
      { wch: 18 },  // MC Payback P50
      { wch: 18 },  // MC Payback P90
      { wch: 22 },  // Prob Positive ROI
      { wch: 22 },  // Prob Payback <12mo
    ];

    // Create workbook with two sheets
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Optimization Opportunities');

    // Add Portfolio Summary sheet if any grounded opportunities exist
    const groundedOpps = analysis.opportunities.filter(o => o.data_source === 'grounded');
    if (groundedOpps.length > 0) {
      const totalAnnualBenefit = groundedOpps.reduce((sum, o) => sum + (Number(o.total_annual_benefit) || 0), 0);
      const totalNPV = groundedOpps.reduce((sum, o) => sum + (Number(o.npv_3year) || 0), 0);
      const avgROI = groundedOpps.reduce((sum, o) => sum + (Number(o.roi_3year_pct) || 0), 0) / groundedOpps.length;
      const withPayback = groundedOpps.filter(o => o.payback_months);
      const avgPayback = withPayback.length > 0
        ? withPayback.reduce((sum, o) => sum + (Number(o.payback_months) || 0), 0) / withPayback.length
        : null;

      const summaryData = [
        { 'Metric': 'Grounded Opportunities', 'Value': groundedOpps.length, 'Unit': 'count' },
        { 'Metric': 'Total Opportunities', 'Value': analysis.opportunities.length, 'Unit': 'count' },
        { 'Metric': 'Total Annual Benefit', 'Value': totalAnnualBenefit, 'Unit': 'USD' },
        { 'Metric': 'Portfolio NPV (3yr)', 'Value': totalNPV, 'Unit': 'USD' },
        { 'Metric': 'Average ROI (3yr)', 'Value': Number(avgROI.toFixed(1)), 'Unit': '%' },
        { 'Metric': 'Average Payback Period', 'Value': avgPayback != null ? Number(avgPayback.toFixed(1)) : 'N/A', 'Unit': 'months' },
        { 'Metric': 'Calculation Engine Version', 'Value': '1.0', 'Unit': '' },
        { 'Metric': 'Discount Rate', 'Value': 10, 'Unit': '%' },
        { 'Metric': 'Analysis Horizon', 'Value': 3, 'Unit': 'years' },
        { 'Metric': 'Export Date', 'Value': new Date().toISOString().split('T')[0], 'Unit': '' },
        { 'Metric': '--- Monte Carlo Sensitivity ---', 'Value': '', 'Unit': '' },
        { 'Metric': 'MC Simulations per Opportunity', 'Value': 10000, 'Unit': 'count' },
        { 'Metric': 'Portfolio NPV P10 (Pessimistic)', 'Value': groundedOpps.reduce((sum, o) => sum + (Number(o.mc_npv_p10) || 0), 0), 'Unit': 'USD' },
        { 'Metric': 'Portfolio NPV P50 (Median)', 'Value': groundedOpps.reduce((sum, o) => sum + (Number(o.mc_npv_p50) || 0), 0), 'Unit': 'USD' },
        { 'Metric': 'Portfolio NPV P90 (Optimistic)', 'Value': groundedOpps.reduce((sum, o) => sum + (Number(o.mc_npv_p90) || 0), 0), 'Unit': 'USD' },
        { 'Metric': 'Avg Prob Positive ROI', 'Value': (() => { const mc = groundedOpps.filter(o => o.mc_prob_positive_roi != null); return mc.length > 0 ? Number((mc.reduce((s, o) => s + Number(o.mc_prob_positive_roi), 0) / mc.length * 100).toFixed(1)) : 'N/A'; })(), 'Unit': '%' },
      ];

      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Portfolio Summary');
    }

    // Generate file name with project name and date
    const projectName = currentProject?.name || 'Project';
    const date = new Date().toISOString().split('T')[0];
    const fileName = `${projectName}_Optimization_Opportunities_${date}.xlsx`;

    // Download
    await XLSX.writeFile(wb, fileName);
  };

  const downloadMetricsTemplate = async () => {
    try {
      // Extract unique process names from opportunities
      const processNamesSet = new Set(
        analysis?.opportunities?.flatMap(opp =>
          opp.affected_steps?.map(step => step.name || step.stepName) || []
        ).filter(Boolean) || []
      );
      const processNames = Array.from(processNamesSet);

      // Extract opportunity names
      const opportunityNames = analysis?.opportunities?.map(opp => opp.title) || [];

      // Generate and download template
      toast.loading('Generating metrics template...', { id: 'metrics-template' });
      await generateMetricsTemplate(processNames, opportunityNames);
      toast.success('Metrics template downloaded! Fill it out with real client data.', { id: 'metrics-template' });
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error('Failed to generate metrics template', { id: 'metrics-template' });
    }
  };

  const handleDownloadAllReports = async () => {
    if (!analysis || !analysis.opportunities || analysis.opportunities.length === 0) {
      toast.error('No opportunities to generate reports for');
      return;
    }

    if (!projectId || !currentProject) {
      toast.error('Project information is required');
      return;
    }

    try {
      // Generate single comprehensive PDF with all opportunities and all sections
      await generateAllOpportunitiesReport(
        parseInt(projectId),
        currentProject.name,
        analysis.opportunities,
        analysis.analysis.total_steps
      );
    } catch (error) {
      console.error('Error generating reports:', error);
      toast.error('Failed to generate comprehensive report');
    }
  };

  const handleDownloadPptx = async () => {
    if (!analysis || !projectId) return;
    setIsDownloadingPptx(true);
    try {
      toast.loading('Generating PowerPoint deck...', { id: 'pptx' });
      await processOptimizationAPI.downloadPptx(parseInt(projectId), analysis.analysis?.project_id ? projectId : undefined);
      toast.success('PowerPoint downloaded!', { id: 'pptx' });
    } catch (error) {
      console.error('Error generating PowerPoint:', error);
      toast.error('Failed to generate PowerPoint', { id: 'pptx' });
    } finally {
      setIsDownloadingPptx(false);
    }
  };

  // Helper function to create process flow data from opportunity
  const createProcessFlowData = (opportunity: OptimizationOpportunity): any => {
    if (!opportunity.affected_steps || opportunity.affected_steps.length === 0) {
      return undefined;
    }

    // Parse time reduction to get percentages
    const timeReductionMatch = opportunity.estimated_time_reduction.match(/(\d+)%/);
    const reductionPercentage = timeReductionMatch ? parseInt(timeReductionMatch[1]) : 30;

    // Create before steps from affected steps
    const beforeSteps = opportunity.affected_steps.map((step: any, index: number) => ({
      id: step.id?.toString() || `step-${index}`,
      name: step.name || step.processStepName || `Step ${index + 1}`,
      description: step.description || '',
      duration: 60, // Default 60 minutes
      manualEffort: 50, // Default 50 minutes
      owner: step.owner || 'Team Member',
      department: step.department || 'Operations',
      isManual: true,
      isBottleneck: index === 0 // Mark first step as bottleneck for demonstration
    }));

    // Calculate totals for before state
    const beforeTotalDuration = beforeSteps.reduce((sum: number, step: any) => sum + step.duration, 0);
    const beforeTotalEffort = beforeSteps.reduce((sum: number, step: any) => sum + step.manualEffort, 0);

    // Create after steps (automated versions)
    const afterSteps = beforeSteps.map((step: any) => ({
      ...step,
      duration: Math.round(step.duration * (1 - reductionPercentage / 100)),
      manualEffort: Math.round(step.manualEffort * (1 - reductionPercentage / 100)),
      isManual: false,
      isAutomated: true,
      isBottleneck: false,
      automationDetails: opportunity.solution_approach
    }));

    const afterTotalDuration = afterSteps.reduce((sum: number, step: any) => sum + step.duration, 0);
    const afterTotalEffort = afterSteps.reduce((sum: number, step: any) => sum + step.manualEffort, 0);

    // Extract compliance controls from compliance requirements if available
    const complianceControls: any[] = [];
    if (opportunity.compliance_requirements) {
      const reqs = opportunity.compliance_requirements as any;

      // SOX Controls
      if (reqs.sox_controls?.applicable || reqs.sox_controls?.controls) {
        const soxControls = reqs.sox_controls.controls || [];
        if (soxControls.length > 0) {
          complianceControls.push({
            type: 'SOX Controls',
            controls: soxControls.slice(0, 3).map((ctrl: any) => ({
              requirement: ctrl.controlName || ctrl.control || 'Control requirement',
              owner: 'Compliance Team',
              implementation: ctrl.description || 'As per SOX requirements'
            }))
          });
        }
      }

      // Data Governance
      if (reqs.data_governance?.applicable) {
        const dgControls = [];
        if (reqs.data_governance.privacyRequirements) {
          dgControls.push({
            requirement: reqs.data_governance.privacyRequirements[0] || 'Data privacy compliance',
            owner: 'Data Governance',
            implementation: 'Implement data protection controls'
          });
        }
        if (dgControls.length > 0) {
          complianceControls.push({
            type: 'Data Governance',
            controls: dgControls
          });
        }
      }

      // Risk Mitigation
      if (reqs.risk_mitigation?.applicable && reqs.risk_mitigation.failureScenarios) {
        complianceControls.push({
          type: 'Risk Controls',
          controls: reqs.risk_mitigation.failureScenarios.slice(0, 2).map((scenario: any) => ({
            requirement: scenario.scenario,
            owner: 'Risk Management',
            implementation: scenario.mitigationControls?.[0] || scenario.mitigation
          }))
        });
      }
    }

    return {
      opportunityTitle: opportunity.title,
      before: {
        steps: beforeSteps,
        totalDuration: beforeTotalDuration,
        totalManualEffort: beforeTotalEffort,
        totalHandoffs: beforeSteps.length - 1,
        bottlenecks: [beforeSteps[0]?.name || 'Initial process step']
      },
      after: {
        steps: afterSteps,
        totalDuration: afterTotalDuration,
        totalManualEffort: afterTotalEffort,
        totalHandoffs: Math.max(0, afterSteps.length - 2),
        automatedSteps: afterSteps.map((s: any) => s.name)
      },
      transformation: {
        timeSaved: beforeTotalDuration - afterTotalDuration,
        timeSavedPercentage: reductionPercentage,
        effortReduction: beforeTotalEffort - afterTotalEffort,
        effortReductionPercentage: reductionPercentage,
        stepsEliminated: 0,
        stepsAutomated: afterSteps.length,
        handoffsReduced: 1
      },
      complianceControls: complianceControls.length > 0 ? complianceControls : undefined
    };
  };

  // Helper function to calculate total time reduction
  const calculateTotalTimeReduction = (opportunities: OptimizationOpportunity[]): string => {
    let totalPercentage = 0;
    let totalHours = 0;
    let percentageCount = 0;
    let hoursCount = 0;

    opportunities.forEach(opp => {
      const timeReduction = opp.estimated_time_reduction;

      // Check for percentage
      const percentageMatch = timeReduction.match(/(\d+)%/);
      if (percentageMatch) {
        totalPercentage += parseInt(percentageMatch[1]);
        percentageCount++;
      }

      // Check for hours
      const hoursMatch = timeReduction.match(/(\d+)(?:-(\d+))?\s*hours?/i);
      if (hoursMatch) {
        const minHours = parseInt(hoursMatch[1]);
        const maxHours = hoursMatch[2] ? parseInt(hoursMatch[2]) : minHours;
        totalHours += (minHours + maxHours) / 2;
        hoursCount++;
      }
    });

    if (percentageCount > 0 && hoursCount > 0) {
      const avgPercentage = Math.round(totalPercentage / percentageCount);
      return `${avgPercentage}% reduction (${Math.round(totalHours)} hours saved)`;
    } else if (percentageCount > 0) {
      const avgPercentage = Math.round(totalPercentage / percentageCount);
      return `${avgPercentage}% reduction`;
    } else if (hoursCount > 0) {
      return `${Math.round(totalHours)} hours saved`;
    } else {
      return 'Significant time savings expected';
    }
  };

  const sortedOpportunities = useMemo(() => {
    if (!analysis?.opportunities) return [];
    return sortByDependencies(analysis.opportunities);
  }, [analysis?.opportunities]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('processOptimization_viewMode', mode);
  };

  const filteredOpportunities = useMemo(() => {
    let opportunities = selectedType === 'all'
      ? sortedOpportunities
      : sortedOpportunities.filter(o => o.inefficiency_type === selectedType);

    if (viewMode === 'compliance') {
      opportunities = opportunities.filter(o =>
        o.regulatory_risk_score && o.regulatory_risk_score >= 7
      );
    }

    return opportunities;
  }, [sortedOpportunities, selectedType, viewMode]);

  const patternBreakdown = analysis?.analysis.patterns_detected || {};

  // Auto-compute wizard step based on data state
  const computedWizardStep = useMemo(() => {
    if (!analysis || analysis.opportunities.length === 0) return 1;
    if (groundedStatus && groundedStatus.total > 0 && groundedStatus.grounded >= groundedStatus.total) return 5;
    if (groundedStatus && groundedStatus.grounded > 0) return 4;
    if (reviewedIds.size >= analysis.opportunities.length) return 3;
    return 2;
  }, [analysis, reviewedIds, groundedStatus]);

  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  // Sync wizardStep with computed step when data changes (auto-advance)
  useEffect(() => {
    setWizardStep(computedWizardStep as 1 | 2 | 3 | 4 | 5);
  }, [computedWizardStep]);

  // Step 2 = AI estimates only (not yet grounded), Step 4 = grounded only
  const stepFilteredOpportunities = useMemo(() => {
    if (wizardStep === 2) return filteredOpportunities.filter(o => o.data_source !== 'grounded');
    if (wizardStep === 4) return filteredOpportunities.filter(o => o.data_source === 'grounded');
    return filteredOpportunities;
  }, [filteredOpportunities, wizardStep]);

  // Show project selection message if no project is available
  if (!projectId) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Lightbulb className="w-16 h-16 mx-auto mb-4 text-purple-400" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Project Selected</h3>
          <p className="text-gray-600">
            Please select a project from the header to view process optimization analysis.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <RefreshCw className="w-12 h-12 animate-spin text-purple-600" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Loading...</h3>
          <p className="text-sm text-gray-600">Please wait...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isWorkflowError = error.includes('workflow') || error.includes('platform');
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-3">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <h3 className="text-lg font-semibold text-red-800">Configuration Required</h3>
        </div>
        <p className="text-red-700 mb-4 text-base">{error}</p>
        <div className="flex items-center gap-3">
          {isWorkflowError && (
            <button
              onClick={() => window.location.href = '/app/admin?tab=workflow-tools'}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow font-medium"
            >
              <Settings className="w-4 h-4" />
              Configure Workflow Tools
            </button>
          )}
          <button
            onClick={loadAnalysis}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }


  // Note: !analysis is handled by the wizard (Step 1 shown). Only early-return when analysis ran but found nothing.
  if (analysis && analysis.opportunities.length === 0) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-8 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
          <CheckCircle className="w-10 h-10 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          No Inefficiencies Detected
        </h3>
        <p className="text-gray-700 mb-6 max-w-xl mx-auto">
          Your process appears to be well-optimized. Analysis found {analysis.analysis.total_steps || 0} steps
          with no obvious structural inefficiencies.
        </p>
        <button
          onClick={() => setShowRegenConfirm(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          Re-generate Analysis
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-super-dark-purple">Process Optimization</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {analysis
              ? <>{analysis.opportunities.length} opportunities · {analysis.analysis.total_steps} steps analyzed</>
              : 'Run AI analysis to get started'}
            {groundedStatus && groundedStatus.total > 0 && (
              <span className="ml-2 text-emerald-600 font-medium">
                · {groundedStatus.grounded}/{groundedStatus.total} grounded
              </span>
            )}
            {reviewedIds.size > 0 && analysis && (
              <span className="ml-2 text-purple-600 font-medium">
                · {reviewedIds.size}/{analysis.opportunities.length} reviewed
              </span>
            )}
          </p>
        </div>
      </div>

      {/* ── Wizard Progress Bar ── */}
      <div className="bg-white border border-gray-200 rounded-2xl px-8 py-5 shadow-sm">
        <div className="relative">
          {/* Background track */}
          {(() => {
            const progressStep = Math.max(computedWizardStep, wizardStep);
            return (
              <div className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${Math.max(0, (progressStep - 1) / 4 * 100)}%` }}
                />
              </div>
            );
          })()}
          {/* Step nodes */}
          <div className="relative grid grid-cols-5">
            {([
              { id: 1 as const, label: 'Analyze',      desc: 'Run AI analysis',       Icon: Lightbulb  },
              { id: 2 as const, label: 'AI Estimates',  desc: 'Review opportunities',  Icon: CheckCircle },
              { id: 3 as const, label: 'Ground',        desc: 'Upload real metrics',   Icon: Database   },
              { id: 4 as const, label: 'Grounded',      desc: 'Review grounded data',  Icon: BarChart3  },
              { id: 5 as const, label: 'Export',        desc: 'Share & download',      Icon: Download   },
            ]).map((step) => {
              const progressStep = Math.max(computedWizardStep, wizardStep);
              const isCompleted = progressStep > step.id;
              const isActive    = wizardStep === step.id;
              const isLocked    = step.id === 4 && (groundedStatus?.grounded ?? 0) === 0;
              return (
                <button
                  key={step.id}
                  onClick={() => !isLocked && setWizardStep(step.id)}
                  title={isLocked ? 'Upload metrics first to unlock Grounded view' : undefined}
                  className={`flex flex-col items-center gap-2.5 py-0.5 group focus:outline-none ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                    isActive
                      ? 'bg-purple-600 text-white ring-4 ring-purple-100 shadow-lg shadow-purple-200'
                      : isCompleted
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-white border-2 border-gray-200 text-gray-400 group-hover:border-gray-300 group-hover:text-gray-500'
                  }`}>
                    {isCompleted && !isActive
                      ? <CheckCircle className="w-5 h-5" />
                      : <step.Icon className="w-4 h-4" />
                    }
                  </div>
                  <div className="text-center">
                    <p className={`text-xs font-semibold leading-tight transition-colors ${
                      isActive ? 'text-purple-700' : isCompleted ? 'text-gray-700' : 'text-gray-400'
                    }`}>{step.label}</p>
                    <p className={`text-[10px] leading-tight mt-0.5 hidden sm:block transition-colors ${
                      isActive ? 'text-purple-400' : 'text-gray-400'
                    }`}>{step.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Step 1: Analyze ── */}
      {wizardStep === 1 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Hero — hidden while analyzing */}
          {!analyzing && (
            <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-white px-10 pt-10 pb-8 text-center border-b border-purple-100">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-sm border border-purple-100 mb-5">
                <Lightbulb className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {analysis ? 'Re-generate AI Analysis' : 'Start AI Analysis'}
              </h2>
              <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
                {analysis
                  ? 'Run a fresh analysis on your workflow documents. This will replace the current results.'
                  : 'AI scans your workflow documents, identifies inefficiencies, and generates actionable optimization recommendations.'}
              </p>
            </div>
          )}
          {/* Body */}
          <div className="px-10 py-8">
            {analyzing ? (
              /* ── Inline analyzing state ── */
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-2xl mb-5 animate-pulse">
                  <RefreshCw className="w-7 h-7 text-purple-600 animate-spin" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Analyzing Process...</h3>
                <p className="text-sm text-gray-500 mb-6">Detecting inefficiency patterns and generating recommendations...</p>
                <div className="max-w-xs mx-auto space-y-2">
                  {[
                    { text: 'Pattern detection',      color: 'bg-blue-500'   },
                    { text: 'ROI estimates',          color: 'bg-green-500'  },
                    { text: 'Compliance analysis',    color: 'bg-orange-500' },
                    { text: 'Implementation roadmap', color: 'bg-purple-500' },
                  ].map(({ text, color }) => (
                    <div key={text} className="flex items-center gap-3 text-left">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div className={`h-full ${color} rounded-full animate-pulse`} style={{ width: '60%' }} />
                      </div>
                      <span className="text-xs text-gray-500 w-36 flex-shrink-0">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-8 max-w-sm mx-auto">
                  {[
                    { Icon: Search,    text: 'Pattern detection',    color: 'text-blue-600',   bg: 'bg-blue-50'   },
                    { Icon: TrendingUp,text: 'ROI estimates',        color: 'text-green-600',  bg: 'bg-green-50'  },
                    { Icon: Shield,    text: 'Compliance analysis',  color: 'text-orange-600', bg: 'bg-orange-50' },
                    { Icon: Map,       text: 'Implementation roadmap', color: 'text-purple-600', bg: 'bg-purple-50' },
                  ].map(({ Icon, text, color, bg }) => (
                    <div key={text} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
                        <Icon className={`w-3.5 h-3.5 ${color}`} />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{text}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => analysis ? setShowRegenConfirm(true) : runAnalysis(false)}
                    className="flex items-center gap-2 px-8 py-3 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 active:bg-purple-800 transition-colors shadow-sm"
                  >
                    {analysis ? 'Re-generate Analysis' : 'Run AI Analysis'}
                  </button>
                  {analysis && (
                    <button onClick={() => setWizardStep(2)} className="px-6 py-3 text-sm text-white font-semibold bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors shadow-sm">
                      View existing →
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Ground ── */}
      {wizardStep === 3 && importHistory.length > 0 && groundedStatus && (() => {
        const latest = importHistory[0];
        const rec = latest.records_imported as any;
        const pct = Number(groundedStatus.percentageGrounded ?? 0);
        const importedAt = new Date(latest.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm">
            <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Upload className="w-4 h-4 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Last Metrics Import</p>
              <p className="text-xs text-gray-400 mt-0.5">{latest.file_name} &bull; {importedAt}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {rec?.laborEconomics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.laborEconomics} labor rates</span>}
                {rec?.processMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.processMetrics} processes</span>}
                {rec?.opportunityMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.opportunityMetrics} opportunities</span>}
                {rec?.opportunitiesUpdated > 0 && <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 font-medium rounded-full">{rec.opportunitiesUpdated} grounded</span>}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5 min-w-[180px]">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Data Quality</span>
                <span className="text-sm font-semibold text-gray-800">{groundedStatus.grounded} / {groundedStatus.total} grounded</span>
                <span className="text-sm font-bold text-emerald-600">{Math.round(pct)}%</span>
                <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"><Upload className="w-3 h-3" /> Import New</button>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })()}
      {wizardStep === 3 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Ground your analysis with real data</h2>
                <p className="text-sm text-gray-500 mt-0.5">Replace AI estimates with actual client metrics — labor rates, process times, and volume data.</p>
              </div>
            </div>
          </div>
          {/* Body */}
          <div className="px-8 py-8">
            {/* Stats cards */}
            {(() => {
              const totalAI      = sortedOpportunities.filter(o => o.data_source !== 'grounded').length;
              const totalGrounded = groundedStatus?.grounded ?? 0;
              const hasGrounded  = totalGrounded > 0;
              return (
                <div className="grid grid-cols-2 gap-3 mb-8">
                  <div className="bg-purple-50 border border-purple-100 rounded-xl px-4 py-4">
                    <p className="text-xs text-gray-500 mb-1">AI Estimates</p>
                    <p className="text-2xl font-bold text-purple-600">{totalAI}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Pending grounding</p>
                  </div>
                  <div className={`${hasGrounded ? 'bg-emerald-50 border-emerald-100' : 'bg-gray-50 border-gray-100'} border rounded-xl px-4 py-4`}>
                    <p className="text-xs text-gray-500 mb-1">Grounded</p>
                    <p className={`text-2xl font-bold ${hasGrounded ? 'text-emerald-600' : 'text-gray-400'}`}>{totalGrounded}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Real data applied</p>
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {/* Step 1 */}
              <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 hover:bg-white hover:border-blue-200 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</div>
                  <p className="text-sm font-semibold text-gray-800">Download the template</p>
                </div>
                <p className="text-xs text-gray-500 mb-4 pl-10">Pre-filled with your process and opportunity names.</p>
                <button onClick={downloadMetricsTemplate} className="w-full py-2.5 bg-white border border-gray-200 text-sm font-medium text-gray-700 rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Download Template
                </button>
              </div>
              {/* Step 2 */}
              <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 hover:bg-white hover:border-blue-200 transition-all group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</div>
                  <p className="text-sm font-semibold text-gray-800">Upload filled template</p>
                </div>
                <p className="text-xs text-gray-500 mb-4 pl-10">Fill in the downloaded template and upload it here.</p>
                <button onClick={() => setIsImportModalOpen(true)} className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                  <Upload className="w-4 h-4" /> Upload Metrics
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button onClick={() => setWizardStep(2)} className="text-sm text-gray-400 hover:text-gray-700 transition-colors">← Back to AI Estimates</button>
              <div className="flex items-center gap-3">
                <button onClick={() => setWizardStep(5)} className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors">
                  Export AI data →
                </button>
                {(groundedStatus?.grounded ?? 0) > 0 && (
                  <button onClick={() => setWizardStep(4)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                    Next: Grounded <ChevronDown className="w-4 h-4 -rotate-90" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 5: Export ── */}
      {wizardStep === 5 && importHistory.length > 0 && groundedStatus && (() => {
        const latest = importHistory[0];
        const rec = latest.records_imported as any;
        const pct = Number(groundedStatus.percentageGrounded ?? 0);
        const importedAt = new Date(latest.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm">
            <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Upload className="w-4 h-4 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Last Metrics Import</p>
              <p className="text-xs text-gray-400 mt-0.5">{latest.file_name} &bull; {importedAt}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {rec?.laborEconomics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.laborEconomics} labor rates</span>}
                {rec?.processMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.processMetrics} processes</span>}
                {rec?.opportunityMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.opportunityMetrics} opportunities</span>}
                {rec?.opportunitiesUpdated > 0 && <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 font-medium rounded-full">{rec.opportunitiesUpdated} grounded</span>}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5 min-w-[180px]">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Data Quality</span>
                <span className="text-sm font-semibold text-gray-800">{groundedStatus.grounded} / {groundedStatus.total} grounded</span>
                <span className="text-sm font-bold text-emerald-600">{Math.round(pct)}%</span>
                <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"><Upload className="w-3 h-3" /> Import New</button>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })()}
      {wizardStep === 5 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Export & share your results</h2>
                <p className="text-sm text-gray-500 mt-0.5">Download reports to share with your team and stakeholders.</p>
              </div>
            </div>
          </div>
          {/* Body */}
          <div className="px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <button onClick={exportToExcel} className="flex flex-col items-center gap-3 p-6 bg-gray-50 border border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-300 hover:shadow-sm transition-all group text-center">
                <div className="w-12 h-12 bg-green-100 group-hover:bg-green-200 rounded-xl flex items-center justify-center transition-colors">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Excel Export</p>
                  <p className="text-xs text-gray-500 mt-0.5">All opportunities with financial data</p>
                </div>
              </button>
              <button onClick={handleDownloadAllReports} className="flex flex-col items-center gap-3 p-6 bg-gray-50 border border-gray-200 rounded-xl hover:bg-purple-50 hover:border-purple-300 hover:shadow-sm transition-all group text-center">
                <div className="w-12 h-12 bg-purple-100 group-hover:bg-purple-200 rounded-xl flex items-center justify-center transition-colors">
                  <FileDown className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">PDF Report</p>
                  <p className="text-xs text-gray-500 mt-0.5">Comprehensive analysis document</p>
                </div>
              </button>
              <button onClick={handleDownloadPptx} disabled={isDownloadingPptx} className="flex flex-col items-center gap-3 p-6 bg-gray-50 border border-gray-200 rounded-xl hover:bg-orange-50 hover:border-orange-300 hover:shadow-sm transition-all group text-center disabled:opacity-50">
                <div className="w-12 h-12 bg-orange-100 group-hover:bg-orange-200 rounded-xl flex items-center justify-center transition-colors">
                  <Monitor className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">PowerPoint</p>
                  <p className="text-xs text-gray-500 mt-0.5">{isDownloadingPptx ? 'Generating...' : 'Presentation deck'}</p>
                </div>
              </button>
              <ExecutiveSummary analysis={analysis!} projectId={parseInt(projectId!)} hideContent={true} />
            </div>
            <button onClick={() => setWizardStep(4)} className="text-sm text-gray-400 hover:text-gray-700 transition-colors">← Back to Grounded Opportunities</button>
          </div>
        </div>
      )}

      {/* Last Metrics Import card — show on Step 2 & 4 when import history exists */}
      {(wizardStep === 2 || wizardStep === 4) && importHistory.length > 0 && groundedStatus && (() => {
        const latest = importHistory[0];
        const rec = latest.records_imported as any;
        const pct = Number(groundedStatus.percentageGrounded ?? 0);
        const importedAt = new Date(latest.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm">
            {/* Icon */}
            <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Upload className="w-4 h-4 text-blue-500" />
            </div>

            {/* Left: file info + tags */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Last Metrics Import</p>
              <p className="text-xs text-gray-400 mt-0.5">{latest.file_name} &bull; {importedAt}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {rec?.laborEconomics > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.laborEconomics} labor rates</span>
                )}
                {rec?.processMetrics > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.processMetrics} processes</span>
                )}
                {rec?.opportunityMetrics > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.opportunityMetrics} opportunities</span>
                )}
                {rec?.opportunitiesUpdated > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 font-medium rounded-full">{rec.opportunitiesUpdated} grounded</span>
                )}
              </div>
            </div>

            {/* Right: data quality + progress + action */}
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5 min-w-[180px]">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Data Quality</span>
                <span className="text-sm font-semibold text-gray-800">{groundedStatus.grounded} / {groundedStatus.total} grounded</span>
                <span className="text-sm font-bold text-emerald-600">{Math.round(pct)}%</span>
                <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"><Upload className="w-3 h-3" /> Import New</button>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Top-level Tabs + Opportunities — Step 2 (AI Estimates) and Step 4 (Grounded) */}
      {(wizardStep === 2 || wizardStep === 4) && (<>

      {/* ── Step 4 Stats Cards ── */}
      {wizardStep === 4 && stepFilteredOpportunities.length > 0 && (() => {
        const totalBenefit = stepFilteredOpportunities.reduce((sum, o) => sum + Number(o.total_annual_benefit ?? 0), 0);
        const totalNPV     = stepFilteredOpportunities.reduce((sum, o) => sum + Number(o.npv_3year ?? 0), 0);
        const avgROI       = stepFilteredOpportunities.filter(o => Number(o.roi_3year_pct ?? 0) > 0).reduce((sum, o, _, arr) => sum + Number(o.roi_3year_pct ?? 0) / arr.length, 0);
        const formatCurrency = (v: number) => v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`;
        const cards = [
          {
            label: 'Grounded',
            value: stepFilteredOpportunities.length,
            sub: 'Real data verified',
            color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100',
          },
          {
            label: 'Annual Benefit',
            value: totalBenefit > 0 ? formatCurrency(totalBenefit) : '—',
            sub: 'Total per year',
            color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100',
          },
          {
            label: '3-Year NPV',
            value: totalNPV > 0 ? formatCurrency(totalNPV) : '—',
            sub: 'Net present value',
            color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100',
          },
          {
            label: 'Avg ROI',
            value: avgROI > 0 ? `${Math.round(avgROI)}%` : '—',
            sub: '3-year return',
            color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-100',
          },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map(({ label, value, sub, color, bg, border }) => (
              <div key={label} className={`${bg} border ${border} rounded-xl px-4 py-4`}>
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        );
      })()}
      {/* ── Step 2 Stats Cards ── */}
      {wizardStep === 2 && stepFilteredOpportunities.length > 0 && (() => {
        const quickWins    = stepFilteredOpportunities.filter(o => o.implementation_complexity === 'low').length;
        const patternTypes = new Set(stepFilteredOpportunities.map(o => o.inefficiency_type)).size;
        const highReadiness = stepFilteredOpportunities.filter(o => (o.automation_readiness_score ?? 0) >= 7).length;
        const cards = [
          {
            label: 'Opportunities',
            value: stepFilteredOpportunities.length,
            sub: 'AI identified',
            color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-100',
          },
          {
            label: 'Quick Wins',
            value: quickWins,
            sub: 'Low complexity',
            color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100',
          },
          {
            label: 'Pattern Types',
            value: patternTypes,
            sub: 'Unique patterns',
            color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100',
          },
          {
            label: 'High Readiness',
            value: highReadiness,
            sub: 'Automation score ≥ 7',
            color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100',
          },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map(({ label, value, sub, color, bg, border }) => (
              <div key={label} className={`${bg} border ${border} rounded-xl px-4 py-4`}>
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="flex items-center border-b border-gray-200">
        <button
          onClick={() => setMainTab('opportunities')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            mainTab === 'opportunities' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Opportunities
        </button>
        <div className="w-px h-4 bg-gray-200 mx-2" />
        {(['matrix', 'roadmap'] as const).map(key => (
          <button
            key={key}
            onClick={() => setMainTab(key)}
            className={`px-4 py-2.5 text-xs border-b-2 transition-colors capitalize ${
              mainTab === key ? 'border-purple-400 text-purple-500 font-medium' : 'border-transparent text-gray-400 hover:text-gray-500'
            }`}
          >
            {key}
          </button>
        ))}
      </div>

      {/* Matrix View */}
      {mainTab === 'matrix' && (
        <PrioritizationMatrix
          opportunities={stepFilteredOpportunities}
          onOpportunityClick={(id) => {
            setMainTab('opportunities');
            setTimeout(() => {
              const el = document.getElementById(`opportunity-${id}`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }}
        />
      )}

      {/* Roadmap View */}
      {mainTab === 'roadmap' && (
        <GanttRoadmap
          opportunities={stepFilteredOpportunities}
          onOpportunityClick={(id) => {
            setMainTab('opportunities');
            setTimeout(() => {
              const el = document.getElementById(`opportunity-${id}`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }}
        />
      )}

      {/* Opportunities List */}
      {mainTab === 'opportunities' && (
      <div className="space-y-3">

        {(() => {
          const stepCount = stepFilteredOpportunities.length;
          const stepReviewed = stepFilteredOpportunities.filter(o => reviewedIds.has(o.id)).length;
          const allStepReviewed = stepCount > 0 && stepReviewed >= stepCount;
          const allGrounded = groundedStatus && groundedStatus.total > 0 && groundedStatus.grounded >= groundedStatus.total;

          return (
            <>
              {/* Step 2: All AI estimates reviewed → nudge to ground */}
              {wizardStep === 2 && allStepReviewed && !allGrounded && (
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-300 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">All AI estimates reviewed!</p>
                      <p className="text-xs text-gray-500 mt-0.5">Upload real metrics to replace AI estimates with grounded calculations.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={downloadMetricsTemplate} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      Download Template
                    </button>
                    <button onClick={() => setIsImportModalOpen(true)} className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-1.5">
                      <Upload className="w-3 h-3" /> Upload Metrics
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: All grounded → nudge to export */}
              {wizardStep === 4 && allGrounded && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-300 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">All opportunities grounded with real data!</p>
                      <p className="text-xs text-gray-500 mt-0.5">Your analysis is complete. Export reports to share with stakeholders.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={exportToExcel} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5">
                      <Download className="w-3 h-3" /> Excel
                    </button>
                    <button onClick={handleDownloadAllReports} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5">
                      <FileDown className="w-3 h-3" /> PDF
                    </button>
                    <button onClick={handleDownloadPptx} disabled={isDownloadingPptx} className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50">
                      <Monitor className="w-3 h-3" /> {isDownloadingPptx ? 'Generating...' : 'PowerPoint'}
                    </button>
                  </div>
                </div>
              )}

              {/* Progress text (Step 2 only) */}
              {wizardStep === 2 && stepCount > 0 && stepReviewed > 0 && stepReviewed < stepCount && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    {`${stepReviewed} of ${stepCount} reviewed — keep going`}
                  </p>
                  {stepReviewed > 0 && (
                    <button onClick={() => setReviewedIds(new Set())} className="text-xs text-gray-400 hover:text-gray-600 transition-colors ml-auto">
                      Clear progress
                    </button>
                  )}
                </div>
              )}
            </>
          );
        })()}

        {stepFilteredOpportunities.length > 0 ? (
          stepFilteredOpportunities.map((opportunity, index) => (
            <div key={opportunity.id} id={`opportunity-${opportunity.id}`}>
              <OpportunityCard
                opportunity={opportunity}
                projectId={parseInt(projectId!)}
                index={index}
                isReviewed={reviewedIds.has(opportunity.id)}
                onMarkReviewed={(id) => setReviewedIds(prev => new Set(Array.from(prev).concat(id)))}
                onOpenImportModal={() => setIsImportModalOpen(true)}
                hideGroundedFinancials={wizardStep === 2}
              />
            </div>
          ))
        ) : wizardStep === 2 ? (
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-2xl p-10 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 rounded-2xl mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">All AI estimates have been grounded!</h3>
            <p className="text-sm text-gray-500 mb-5">No ungrounded opportunities remain. Head to Step 4 to review the real financial data.</p>
            <button onClick={() => setWizardStep(4)} className="px-6 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors">
              View Grounded Opportunities →
            </button>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl p-12 text-center">
            {viewMode === 'compliance' ? (
              <div className="max-w-2xl mx-auto">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-green-100 rounded-full">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  No High-Risk Compliance Items Detected
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Compliance View shows automation opportunities that require regulatory review, including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-semibold text-red-600 mb-1">MRM Tier 1-2</div>
                    <div className="text-xs text-gray-600">AI/ML models requiring validation</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-semibold text-orange-600 mb-1">SOD Violations</div>
                    <div className="text-xs text-gray-600">Segregation of duties conflicts</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-semibold text-yellow-600 mb-1">High Risk Items</div>
                    <div className="text-xs text-gray-600">Critical or high severity issues</div>
                  </div>
                </div>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setViewMode('all');
                      localStorage.setItem('processOptimization_viewMode', 'all');
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    View All Opportunities
                  </button>
                  <button
                    onClick={() => {
                      setViewMode('business');
                      localStorage.setItem('processOptimization_viewMode', 'business');
                    }}
                    className="px-4 py-2 bg-white text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Switch to Business View
                  </button>
                </div>
              </div>
            ) : (
              <div className="max-w-md mx-auto">
                <div className="mb-4 flex justify-center">
                  <div className="p-3 bg-gray-200 rounded-full">
                    <AlertCircle className="w-10 h-10 text-gray-500" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Opportunities Match Filter
                </h3>
                <p className="text-gray-600">
                  Try selecting a different pattern type or view mode to see more opportunities.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      )}

        {/* Footer nav — different per step */}
        {wizardStep === 2 && (
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setWizardStep(1)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Re-analyze</button>
            <button onClick={() => setWizardStep(3)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
              Next: Ground Data <ChevronDown className="w-4 h-4 -rotate-90" />
            </button>
          </div>
        )}
        {wizardStep === 4 && (
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setWizardStep(3)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Back to Ground</button>
            <button onClick={() => setWizardStep(5)} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors">
              Next: Export <ChevronDown className="w-4 h-4 -rotate-90" />
            </button>
          </div>
        )}
      </>)} {/* end wizardStep === 2 or 4 */}

      {/* Re-generate Confirmation Dialog */}
      {showRegenConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Re-generate Analysis?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will discard the current analysis and run a fresh AI analysis on your project documents. All existing opportunities and their data will be replaced.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRegenConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowRegenConfirm(false); runAnalysis(true); }}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                Yes, Re-generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Import Modal */}
      <MetricsImportModal
        projectId={parseInt(projectId!)}
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default ProcessOptimization;
